import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Snackbar,
  alpha,
  Divider,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import API from '../api/axios';
import InquiryForm from '../components/InquiryForm';

/**
 * Convert any Google Maps link (short link, share link, place link) into an
 * embeddable URL so it renders inside an iframe. Proper embed URLs are kept
 * unchanged. Used as a client-side safety net on top of the server normalization.
 */
const toMapEmbedUrl = (url = '') => {
  const trimmed = (url || '').trim();
  if (!trimmed) return '';

  // Already an embeddable URL -> keep it unchanged.
  if (trimmed.includes('output=embed') || trimmed.includes('/maps/embed')) return trimmed;

  // Extract coordinates: @lat,lng / !3d...!4d... / ?q=lat,lng
  let m = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (!m) m = trimmed.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (!m) m = trimmed.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (m) return `https://maps.google.com/maps?q=${m[1]},${m[2]}&z=17&output=embed`;

  // Fall back to a query-based embed when an address query is present.
  m = trimmed.match(/[?&]q=([^&]+)/);
  if (m) return `https://maps.google.com/maps?q=${encodeURIComponent(decodeURIComponent(m[1]))}&output=embed`;

  return trimmed;
};

// Treat a URL as embeddable only when it is a proper Google Maps embed URL.
const canEmbedMap = (url = '') => {
  const u = (url || '').toLowerCase();
  return u.includes('output=embed') || u.includes('/maps/embed');
};

// Build a full address string (used to embed a map by address as a fallback).
const fullAddressOf = (location = {}) =>
  [location.address, location.city, location.country].filter(Boolean).join(', ');

const Contact = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const { data } = await API.get('/locations');
      setLocations(data);
    } catch {
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%)',
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 8, md: 10 },
        }}
      >
        <Box sx={{ position: 'absolute', top: '20%', left: '5%', width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(102,126,234,0.2) 0%, transparent 70%)' }} />
        <Box sx={{ position: 'absolute', bottom: '10%', right: '10%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(118,75,162,0.2) 0%, transparent 70%)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', mb: 3 }}>
            <ContactSupportIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: 'white', mb: 2, fontSize: { xs: '2rem', md: '2.75rem' } }}>
            Get in Touch
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 300, maxWidth: 500, mx: 'auto', lineHeight: 1.7 }}>
            Have a question or ready to plan your trip? We'd love to hear from you.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Contact Form */}
          <Grid item xs={12} md={5}>
            <Box sx={{ position: { xs: 'static', md: 'sticky' }, top: { md: 24 } }}>
              <InquiryForm variant="desktop" />
            </Box>
          </Grid>

          {/* Locations */}
          <Grid item xs={12} md={7}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Our Offices</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
              Visit us at any of our branches across multiple locations.
            </Typography>

            {loading && (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            )}
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

            {!loading && !error && locations.length === 0 && (
              <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No locations available at the moment.
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {locations.map((location) => (
                <Card
                  key={location.id ?? location._id}
                  elevation={0}
                  sx={{
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': { boxShadow: '0 12px 40px rgba(0,0,0,0.1)', transform: 'translateY(-3px)' },
                  }}
                >
                  {/* Map */}
                  {(() => {
                    let mapEmbedSrc = toMapEmbedUrl(location.mapEmbedUrl);

                    // If the pasted link cannot be turned into an embed (e.g. a short link
                    // that browsers block resolving client-side), build an embed from the
                    // full address instead so a map always shows.
                    if (!canEmbedMap(mapEmbedSrc)) {
                      const addr = fullAddressOf(location);
                      if (addr) {
                        mapEmbedSrc = `https://maps.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`;
                      }
                    }

                    if (!canEmbedMap(mapEmbedSrc)) {
                      // No usable map source at all -> show a link fallback instead of a blank iframe.
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            py: 6,
                            px: 2,
                            bgcolor: 'rgba(102,126,234,0.06)',
                            color: 'text.secondary',
                            textAlign: 'center',
                          }}
                        >
                          <LocationOnIcon sx={{ fontSize: 44, color: 'primary.main', mb: 1 }} />
                          <Typography variant="body2">Map preview not available</Typography>
                          <Button
                            component="a"
                            href={mapEmbedSrc}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 2, mt: 1 }}
                          >
                            Open in Google Maps
                          </Button>
                        </Box>
                      );
                    }

                    return (
                      <Box sx={{ position: 'relative', paddingBottom: '45%', height: 0, overflow: 'hidden' }}>
                        <Box
                          component="iframe"
                          src={mapEmbedSrc}
                          title={`${location.name} Location`}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                        />
                      </Box>
                    );
                  })()}

                  {/* Info */}
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                      {location.name}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: alpha('#667eea', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <LocationOnIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {location.address}<br />{location.city}, {location.country}
                        </Typography>
                      </Box>
                      {location.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: alpha('#667eea', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">{location.phone}</Typography>
                        </Box>
                      )}
                      {location.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: alpha('#667eea', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <EmailIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">{location.email}</Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;